import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import Logo from "@/components/Logo";
import {
    LogOut,
    RefreshCw,
    Mail,
    Phone,
    MapPin,
    Trash2,
    Search,
    Lock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "capdistrib_admin_token";

const STATUS_OPTIONS = [
    { value: "new", label: "Nouveau" },
    { value: "contacted", label: "Contacté" },
    { value: "qualified", label: "Qualifié" },
    { value: "rejected", label: "Rejeté" },
];

const LEVEL_LABEL = {
    apporteur: "Apporteur",
    business_partner: "Business Partner",
    master_partner: "Master Partner",
};

const STATUS_STYLES = {
    new: "bg-cap-violet/10 text-cap-violet ring-cap-violet/20",
    contacted: "bg-cap-gold/15 text-cap-goldDeep ring-cap-gold/30",
    qualified: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    rejected: "bg-rose-50 text-rose-700 ring-rose-200",
};

export default function AdminPage() {
    const [token, setToken] = useState(
        () => localStorage.getItem(TOKEN_KEY) || "",
    );
    const [authed, setAuthed] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [apps, setApps] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filterLevel, setFilterLevel] = useState("all");
    const [filterStatus, setFilterStatus] = useState("all");
    const [q, setQ] = useState("");

    const headers = useCallback(
        () => ({ "X-Admin-Token": token }),
        [token],
    );

    const verify = useCallback(
        async (t) => {
            setVerifying(true);
            try {
                await axios.post(
                    `${API}/admin/verify`,
                    {},
                    { headers: { "X-Admin-Token": t } },
                );
                localStorage.setItem(TOKEN_KEY, t);
                setAuthed(true);
            } catch {
                toast.error("Token administrateur invalide.");
                setAuthed(false);
            } finally {
                setVerifying(false);
            }
        },
        [],
    );

    useEffect(() => {
        const t = localStorage.getItem(TOKEN_KEY);
        if (t) {
            setToken(t);
            verify(t);
        }
    }, [verify]);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [a, s] = await Promise.all([
                axios.get(`${API}/applications`, { headers: headers() }),
                axios.get(`${API}/applications/stats`, { headers: headers() }),
            ]);
            setApps(a.data);
            setStats(s.data);
        } catch (e) {
            toast.error("Impossible de charger les candidatures.");
        } finally {
            setLoading(false);
        }
    }, [headers]);

    useEffect(() => {
        if (authed) fetchAll();
    }, [authed, fetchAll]);

    const refreshStats = useCallback(async () => {
        try {
            const s = await axios.get(`${API}/applications/stats`, {
                headers: headers(),
            });
            setStats(s.data);
        } catch {
            /* silent */
        }
    }, [headers]);

    const updateStatus = async (id, status) => {
        try {
            const { data } = await axios.patch(
                `${API}/applications/${id}`,
                { status },
                { headers: headers() },
            );
            setApps((arr) => arr.map((a) => (a.id === id ? data : a)));
            refreshStats();
            toast.success("Statut mis à jour");
        } catch {
            toast.error("Échec de la mise à jour");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer définitivement cette candidature ?")) return;
        try {
            await axios.delete(`${API}/applications/${id}`, { headers: headers() });
            setApps((arr) => arr.filter((a) => a.id !== id));
            refreshStats();
            toast.success("Candidature supprimée");
        } catch {
            toast.error("Échec de la suppression");
        }
    };

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setAuthed(false);
        setApps([]);
        setStats(null);
    };

    if (!authed) {
        return (
            <AdminLogin
                token={token}
                setToken={setToken}
                onSubmit={() => verify(token)}
                loading={verifying}
            />
        );
    }

    const filtered = apps.filter((a) => {
        if (filterLevel !== "all" && a.level !== filterLevel) return false;
        if (filterStatus !== "all" && a.status !== filterStatus) return false;
        if (q) {
            const t = q.toLowerCase();
            const hay = `${a.first_name} ${a.last_name} ${a.email} ${a.country_region} ${a.network_type}`.toLowerCase();
            if (!hay.includes(t)) return false;
        }
        return true;
    });

    return (
        <div className="min-h-screen bg-cap-light" data-testid="admin-page">
            {/* Header */}
            <header className="bg-cap-ink text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Logo variant="white" />
                        <span className="hidden sm:inline-block text-[12px] uppercase tracking-[0.18em] text-white/55">
                            Admin Console
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={fetchAll}
                            className="px-3 py-2 rounded-full text-[13px] text-white/80 hover:text-white inline-flex items-center gap-1.5"
                            data-testid="admin-refresh"
                        >
                            <RefreshCw size={14} />
                            Actualiser
                        </button>
                        <button
                            onClick={logout}
                            className="px-3 py-2 rounded-full text-[13px] text-white/80 hover:text-white inline-flex items-center gap-1.5"
                            data-testid="admin-logout"
                        >
                            <LogOut size={14} />
                            Déconnexion
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <h1 className="h-display text-3xl font-bold text-cap-ink">
                            Candidatures
                        </h1>
                        <p className="text-cap-textMuted mt-1">
                            Vue d'ensemble des candidatures partenaires.
                        </p>
                    </div>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="mt-7 grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard
                            label="Total"
                            value={stats.total}
                            testid="stat-total"
                        />
                        <StatCard
                            label="Nouveaux"
                            value={stats.by_status.new}
                            testid="stat-new"
                        />
                        <StatCard
                            label="Business Partner"
                            value={stats.by_level.business_partner}
                            testid="stat-bp"
                        />
                        <StatCard
                            label="Master Partner"
                            value={stats.by_level.master_partner}
                            testid="stat-master"
                        />
                    </div>
                )}

                {/* Filters */}
                <div className="mt-8 flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[220px] max-w-md">
                        <Search
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-cap-textMuted"
                        />
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Rechercher (nom, email, région)…"
                            className="pl-9 bg-white"
                            data-testid="admin-search"
                        />
                    </div>
                    <Select value={filterLevel} onValueChange={setFilterLevel}>
                        <SelectTrigger
                            className="w-[200px] bg-white"
                            data-testid="filter-level"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous niveaux</SelectItem>
                            <SelectItem value="apporteur">Apporteur</SelectItem>
                            <SelectItem value="business_partner">
                                Business Partner
                            </SelectItem>
                            <SelectItem value="master_partner">
                                Master Partner
                            </SelectItem>
                        </SelectContent>
                    </Select>
                    <Select
                        value={filterStatus}
                        onValueChange={setFilterStatus}
                    >
                        <SelectTrigger
                            className="w-[180px] bg-white"
                            data-testid="filter-status"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous statuts</SelectItem>
                            {STATUS_OPTIONS.map((s) => (
                                <SelectItem key={s.value} value={s.value}>
                                    {s.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Table */}
                <div className="mt-6 bg-white border border-cap-border rounded-2xl overflow-hidden">
                    <Table data-testid="admin-table">
                        <TableHeader>
                            <TableRow className="bg-cap-light">
                                <TableHead>Candidat</TableHead>
                                <TableHead>Contact</TableHead>
                                <TableHead>Niveau</TableHead>
                                <TableHead>Vertical</TableHead>
                                <TableHead>Statut</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-10 text-cap-textMuted">
                                        Chargement…
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-12 text-cap-textMuted">
                                        Aucune candidature pour ces filtres.
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading &&
                                filtered.map((a) => (
                                    <TableRow
                                        key={a.id}
                                        className="hover:bg-cap-light/60"
                                        data-testid={`admin-row-${a.id}`}
                                    >
                                        <TableCell>
                                            <div className="font-semibold text-cap-ink">
                                                {a.first_name} {a.last_name}
                                            </div>
                                            <div className="text-[12px] text-cap-textMuted">
                                                {formatDate(a.created_at)}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col gap-1 text-[13px]">
                                                <a
                                                    href={`mailto:${a.email}`}
                                                    className="inline-flex items-center gap-1.5 text-cap-violet hover:underline"
                                                >
                                                    <Mail size={13} />
                                                    {a.email}
                                                </a>
                                                <span className="inline-flex items-center gap-1.5 text-cap-text/80">
                                                    <Phone size={13} />
                                                    {a.phone}
                                                </span>
                                                <span className="inline-flex items-center gap-1.5 text-cap-textMuted">
                                                    <MapPin size={13} />
                                                    {a.country_region}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-[13px] font-medium text-cap-ink">
                                                {LEVEL_LABEL[a.level] || a.level}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-[13px] text-cap-text/85">
                                            <div>{a.network_type}</div>
                                            <div className="text-[12px] text-cap-textMuted line-clamp-2 max-w-xs">
                                                {a.network_description}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Select
                                                value={a.status}
                                                onValueChange={(v) =>
                                                    updateStatus(a.id, v)
                                                }
                                            >
                                                <SelectTrigger
                                                    className={`h-8 w-[140px] text-[12px] font-semibold ring-1 ${STATUS_STYLES[a.status] || ""}`}
                                                    data-testid={`status-select-${a.id}`}
                                                >
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {STATUS_OPTIONS.map((s) => (
                                                        <SelectItem
                                                            key={s.value}
                                                            value={s.value}
                                                        >
                                                            {s.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <button
                                                onClick={() => remove(a.id)}
                                                className="text-rose-600 hover:bg-rose-50 p-2 rounded-full"
                                                data-testid={`delete-btn-${a.id}`}
                                                aria-label="Supprimer"
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </div>
            </main>
        </div>
    );
}

function StatCard({ label, value, testid }) {
    return (
        <div
            className="bg-white border border-cap-border rounded-xl p-5"
            data-testid={testid}
        >
            <p className="text-[12px] uppercase tracking-[0.16em] text-cap-textMuted font-semibold">
                {label}
            </p>
            <p className="h-display text-3xl font-bold text-cap-ink mt-1.5">
                {value}
            </p>
        </div>
    );
}

function AdminLogin({ token, setToken, onSubmit, loading }) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-cap-ink relative overflow-hidden px-4">
            <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
            <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-cap-violet/30 blur-[120px]" />

            <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 border border-white/20">
                <div className="flex flex-col items-center text-center">
                    <Logo variant="navy" />
                    <div className="mt-6 w-12 h-12 rounded-xl bg-cap-ink/5 ring-1 ring-cap-ink/10 flex items-center justify-center">
                        <Lock className="text-cap-ink" size={20} />
                    </div>
                    <h1 className="h-display text-2xl font-bold text-cap-ink mt-5">
                        Accès Administration
                    </h1>
                    <p className="text-cap-textMuted mt-2 text-[14px]">
                        Saisissez le jeton administrateur pour accéder aux
                        candidatures.
                    </p>
                </div>

                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        onSubmit();
                    }}
                    className="mt-7 space-y-3"
                >
                    <Input
                        type="password"
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        placeholder="Token administrateur"
                        data-testid="admin-token-input"
                    />
                    <button
                        type="submit"
                        disabled={loading || !token}
                        className="btn-navy w-full py-3 rounded-full text-sm font-semibold disabled:opacity-60"
                        data-testid="admin-login-button"
                    >
                        {loading ? "Vérification…" : "Se connecter"}
                    </button>
                </form>
                <p className="mt-5 text-[12px] text-cap-textMuted text-center">
                    Espace réservé à l'équipe CapDistrib.
                </p>
            </div>
        </div>
    );
}

function formatDate(s) {
    try {
        const d = new Date(s);
        return d.toLocaleString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return s;
    }
}
